import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { RouterProvider } from 'react-router-dom';

import './index.css';
import '@era-ui/globals.css';

import { store, persistor } from './app/store';
import { useUserData } from './hooks/useUserData';
import { useInvokeCallbackOnceWhen } from './hooks/useInvokeCallbackOnceWhen';
import { initGA } from './ga';
import { disableConsoleWarningsInProduction } from './utils';
import { ConfirmModalProvider } from './components/ui/Modal/ConfirmModalProvider';

import * as Sentry from '@sentry/react';
import router from './routes';

// Disable console warnings in production
disableConsoleWarningsInProduction();

const App = () => {
  const user = useUserData();

  useInvokeCallbackOnceWhen(() => {
    initGA({ userID: user?.eraUserId || '' });
  }, !!user?.eraUserId || !!!user);

  return (
    <Provider store={store}>
      <PersistGate
        persistor={persistor}
        loading={'...'}
      >
        <ConfirmModalProvider>
          <RouterProvider
            router={router}
            fallbackElement={<p>Initial Load...</p>}
          />
        </ConfirmModalProvider>
      </PersistGate>
    </Provider>
  );
};

Sentry.init({
  dsn: 'https://12a1ac299fe8e7e02fc8c14dbe4db786@o4504405282127872.ingest.us.sentry.io/4508753579081728',
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  tracesSampleRate: 1.0,
  tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  enabled: process.env.NODE_ENV === 'production',
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
